import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile as updateFirebaseAuthProfile
} from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { createUserProfile, getUserProfile } from '../services/userService';
import { doc, updateDoc } from 'firebase/firestore';

// Creazione del contesto
const AuthContext = createContext();

// Hook personalizzato per usare il contesto
export function useAuth() {
  return useContext(AuthContext);
}

// Provider dell'autenticazione
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Funzione per la registrazione
  async function signup(email, password, displayName) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateFirebaseAuthProfile(userCredential.user, { displayName });
      
      // Crea il profilo utente in Firestore
      await createUserProfile(userCredential.user.uid, {
        email,
        displayName,
        emailVerified: false
      });

      return userCredential;
    } catch (error) {
      throw error;
    }
  }

  // Funzione per il login
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Funzione per il logout
  function logout() {
    return signOut(auth);
  }

  // Funzione per il reset della password
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Aggiorna il profilo utente in Firestore
  async function updateUserProfile(profileData) {
    if (!currentUser) {
      throw new Error("Nessun utente loggato.");
    }
    const userDocRef = doc(db, "users", currentUser.uid);
    await updateDoc(userDocRef, profileData);
    
    // Aggiorna lo stato locale del profilo utente
    setUserProfile(prevProfile => ({ ...prevProfile, ...profileData }));
  }

  // Effetto per monitorare lo stato dell'utente
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Recupera il profilo utente da Firestore
        const profile = await getUserProfile(user.uid);
        setUserProfile(profile);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    // Cleanup al dismontaggio del componente
    return unsubscribe;
  }, []);

  // Valore da condividere attraverso il contesto
  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    // TODO: Aggiungi la funzione per l'upload dell'avatar qui
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export default AuthContext; 